import instance from '../api'
import { BranchTypes, OrganizationTypes } from '../../interfaces/organization'

/**
 * The organisation's branches, or null when they cannot be read.
 *
 * `GET /organizations/organization/` (the list action) is served by
 * `OrganizationWithSubscriptionListSerializer`, which does not override
 * `to_representation` and so omits `branch` entirely. Only
 * `OrganizationSerializer` attaches it, and `by-code/` is the last endpoint
 * still using that serializer - hence this second request.
 */
const getBranches = async (code: string): Promise<BranchTypes[] | null> => {
  try {
    const response = await instance.get(
      `/organizations/organization/by-code/${code}/`,
    )
    return response.data.data?.branch ?? null
  } catch {
    // Branches are secondary: losing them must not take the organisation -
    // navigation, company code, logo - down with it.
    return null
  }
}

/** The signed-in user's own organisation, with its branches attached. */
const fetchSelectedOrg = async (
  organizationId: number,
  token: string,
): Promise<OrganizationTypes | null> => {
  const response = await instance.get('/organizations/organization/', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  const organizations: OrganizationTypes[] = response.data.data
  const organization = organizations?.find((org) => org.id === organizationId)
  if (!organization) return null

  // The moment the list serializer carries branches itself - whatever shape it
  // picks, including an empty list - this second request stops happening, with
  // no frontend change needed. Only the key being absent means "ask by-code".
  if (organization.branch !== undefined) return organization
  if (!organization.code) return { ...organization, branch: null }

  return { ...organization, branch: await getBranches(organization.code) }
}

export default fetchSelectedOrg
